"use client";

import { caseMaterialLabel } from "@/utils/case-material";

import SectionLayout from "@/components/ui/SectionLayout";
import React, { useEffect, useState } from "react";
import { CaseSectionProps } from "../model";
import { getDefaultCmsSection } from "@/utils/helper";
import CaseAgent from "@/components/ui/CaseAgent";
import Card from "@/components/ui/Card";
import { motion } from "motion/react";
import OpenHouseForm from "@/components/forms/OpenhouseForm";
import DownloadDocumentForm from "@/components/forms/DownloadDocumentForm";
import ViewingForm from "@/components/forms/ViewingForm";
import BidForm from "@/components/forms/BidForm";
import StoreContactDetails from "@/components/ui/StoreContactDetails";
const CaseContactSection: React.FC<
  CaseSectionProps<{
    showAgent?: boolean;
    downloadFormTextColor?: string;
  }>
> = ({ cmsSection, caseData, appData, cmsData, pageData, pageCtx }) => {
  const tab = pageCtx.searchParams.tab;
  const [selectedTab, setSelectedTab] = useState(tab ?? "viewing");
  const tabs = [
    {
      id: "viewing",
      label: "Bestil fremvisning",
    },
  ];

  if (caseData.openhouses?.filter((x) => x.bookingRequired).length > 0) {
    tabs.push({
      id: "openhouse",
      label: "Tilmeld åbent hus",
    });
  }

  if (caseData.documents?.length > 0) {
    tabs.push({
      id: "documents",
      label: caseMaterialLabel(caseData, "Bestil salgsmateriale"),
    });
  }

  if (caseData.canSendBid) {
    tabs.push({
      id: "bid",
      label: "Giv et bud",
    });
  }

  useEffect(() => {
    if (!window) {
      return;
    }

    const handleSelectedTabEvent = (event: any) => {
      setSelectedTab(event.detail.selectedTab);
    };

    window.addEventListener("selectedTabEvent", handleSelectedTabEvent);

    if (tab) {
      const caseContactSectionElement =
        document.getElementById("caseContactSection");
      if (caseContactSectionElement) {
        caseContactSectionElement.scrollIntoView({ behavior: "smooth" });
      }
    }

    return () => {
      window.removeEventListener("selectedTabEvent", handleSelectedTabEvent);
    };
  }, []);

  return (
    <SectionLayout cmsSection={getDefaultCmsSection(cmsSection)}>
      <div id="caseContactSection" className="flex flex-col md:flex-row gap-4">
        <div className="max-w-96 w-full md:w-1/3">
          {caseData.agent &&
          (cmsSection.specifics.showAgent ||
            pageData.showAgent ||
            (typeof pageData.showAgent === "undefined" &&
              cmsData.showAgents)) ? (
            <Card>
              <CaseAgent
                contentPadding={true}
                cmsSection={cmsSection}
                caseData={caseData}
              />
            </Card>
          ) : (
            <StoreContactDetails caseData={caseData} />
          )}
        </div>
        <div className="flex-1">
          {caseData.isActive && (
            <nav>
              <ul className="flex">
                {tabs.map((item, index) => {
                  const isSelected = selectedTab === item.id;
                  return (
                    <li
                      key={index}
                      role="tab"
                      className="relative bg-[var(--tabs-bg-color)] first:homepage-card-tl-rounded last:homepage-card-tr-rounded overflow-hidden"
                      aria-selected={isSelected}
                    >
                      {isSelected ? (
                        <motion.div
                          layoutId="selected"
                          className="absolute bg-[var(--tabs-active-bg-color,white)] inset-0 z-1"
                        />
                      ) : null}
                      <motion.button
                        data-active={isSelected}
                        className="z-2 homepage-text-sm relative cursor-pointer p-4 md:p-4 data-[active=true]:bg-[var(--tabs-active-bg-color)] data-[active=false]:text-[var(--tabs-text-color)] data-[active=true]:text-[var(--tabs-active-text-color)]"
                        onTapStart={() => setSelectedTab(item.id)}
                        whileTap={{ scale: 0.9 }}
                        whileFocus={{
                          backgroundColor: "var(--accent-transparent)",
                        }}
                      >
                        {item?.label}
                      </motion.button>
                    </li>
                  );
                })}
              </ul>
              <div className="bg-[var(--tabs-active-bg-color)] p-4 homepage-text-sm homepage-card-tr-rounded homepage-card-br-rounded homepage-card-bl-rounded">
                {selectedTab === "viewing" && (
                  <ViewingForm caseData={caseData!} cmsData={cmsData} />
                )}
                {selectedTab === "openhouse" &&
                  caseData?.openhouses &&
                  caseData.openhouses.filter((x) => x.bookingRequired).length >
                    0 && (
                    <OpenHouseForm
                      caseData={caseData!}
                      cmsData={cmsData}
                      inTabPanel
                    />
                  )}
                {selectedTab === "documents" && (
                  <DownloadDocumentForm
                    caseData={caseData}
                    appData={appData}
                    cmsData={cmsData}
                    pageData={pageData}
                    cmsSection={cmsSection}
                  />
                )}
                {selectedTab === "bid" && (
                  <BidForm caseData={caseData} cmsData={cmsData} />
                )}
              </div>
            </nav>
          )}
        </div>
      </div>
    </SectionLayout>
  );
};

export default CaseContactSection;
